import { autoinject } from "aurelia-framework";
import { GhgRoutes } from "../../constants/ghg/ghg.constants";
import {
    ComputeRevisedGhgRequest,
    ComputeRevisedGhgResponse,
    DoesVesselCiiExistRequest,
    EmailValidationRequest,
    EnvironmentalVerificationSettings,
    GenerateExternalGhgVerificationRequest,
    GetClassificationSocietyResponse,
    GetEstimatedCiiResponse,
    GetGhgVerificationCiiResponse,
    GetHasUnacceptableCiiRatingsResponse,
    GetHullPreparationStandardResponse,
    GetPaintManufacturersResponse,
    GetPaintTypesResponse,
    GetVesselPendingGhgVerificationResponse,
    GhgRatingFactorSourceCommentResponse,
    GhgVerificationCiiFactorClientModel,
    GhgVerificationClientModel,
    GhgVerificationCorrespondenceEmail,
    GhgVerificationEexiFactorClientModel,
    GhgVerificationEnergySavingEquipmentModel,
    GhgVerificationModel,
    GhgVerificationRatingFactorClientModel,
    GhgVerificationStatusClientModel,
    SaveClientGhgVerificationRequest,
    SaveGhgEnergySavingEquipmentCommentRequest,
    SaveGhgEnergySavingEquipmentCommentResponse,
    SaveGhgRatingFactorSourceCommentRequest,
    SaveGhgVerificationCiiCommentRequest,
    SaveGhgVerificationCiiCommentResponse,
    SaveGhgVerificationCiiRequest,
    SaveGhgVerificationCiiResponse,
    SaveGhgVerificationEexiCommentRequest,
    SaveGhgVerificationEexiCommentResponse,
    SaveGhgVerificationEexiRequest,
    SaveGhgVerificationEexiResponse,
    SaveGhgVerificationEnergySavingEquipmentsRequest,
    SaveGhgVerificationEnergySavingEquipmentsResponse,
    SaveGhgVerificationRatingFactorsRequest,
    SaveGhgVerificationRatingFactorsResponse,
    SaveGhgVerificationRequest,
    SaveGhgVerificationResponse,
    SendInviteToDocRequest,
    SendInviteToDocResponse,
    SubmitGhgRatingChangeRequest,
    SubmitGhgRatingChangeResponse
} from "../../models/ghgs/ghg.models";
import { ValidationResult } from "../../models/validations/validations.models";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";

@autoinject
export class GhgService {

    constructor(private requestClient: RequestClientService) { }

    public getVesselPendingGhgVerification(vesselId: number): Promise<GetVesselPendingGhgVerificationResponse> {
        return this.requestClient.get<GetVesselPendingGhgVerificationResponse>(GhgRoutes.GetVesselPendingGhgVerification, { vesselId });
    }

    public getGhgVerification(ghgVerificationId: number): Promise<GhgVerificationModel> {
        return this.requestClient.get<GhgVerificationModel>(GhgRoutes.GetGhgVerification, { ghgVerificationId });
    }

    public getGhgVerificationClientModel(ghgVerificationId: number): Promise<GhgVerificationClientModel> {
        return this.requestClient.get<GhgVerificationClientModel>(GhgRoutes.GetGhgVerificationClientModel, { ghgVerificationId });
    }

    public getGhgCorrespondenceEmails(id: number): Promise<GhgVerificationCorrespondenceEmail[]> {
        return this.requestClient.get<GhgVerificationCorrespondenceEmail[]>(`${GhgRoutes.GetGhgCorrespondenceEmails}/${id}`, { });
    }

    public getGhgVerificationByGuid(guid: string): Promise<GhgVerificationModel> {
        return this.requestClient.get<GhgVerificationModel>(`${GhgRoutes.GetGhgVerificationByGuid}/${guid}`, { });
    }

    public getGhgVerificationRatingFactorsClientModel(ghgVerificationId: number): Promise<GhgVerificationRatingFactorClientModel[]> {
        return this.requestClient.get(GhgRoutes.GetGhgVerificationRatingFactorsClientModel, { ghgVerificationId });
    }

    public getGhgVerificationEexiFactorsClientModel(ghgVerificationId: number): Promise<GhgVerificationEexiFactorClientModel[]> {
        return this.requestClient.get(GhgRoutes.GetGhgVerificationEexiFactorsClientModel, { ghgVerificationId });
    }

    public getGhgVerificationCiiFactorsClientModel(ghgVerificationId: number): Promise<GhgVerificationCiiFactorClientModel[]> {
        return this.requestClient.get(GhgRoutes.GetGhgVerificationCiiFactorsClientModel, { ghgVerificationId });
    }

    public getGhgVerificationEnergySavingEquipments(ghgVerificationId: number): Promise<GhgVerificationEnergySavingEquipmentModel[]> {
        return this.requestClient.get(GhgRoutes.GetGhgVerificationEnergySavingEquipments, { ghgVerificationId });
    }

    public getGhgVerificationCii(ghgVerificationId: number): Promise<GetGhgVerificationCiiResponse> {
        return this.requestClient.get(GhgRoutes.GetGhgVerificationCii, { ghgVerificationId });
    }

    public getGhgVerificationStatusClientModel(ghgVerificationId: number): Promise<GhgVerificationStatusClientModel> {
        return this.requestClient.get(GhgRoutes.GetGhgVerificationStatusClientModel, { ghgVerificationId });
    }

    public saveGhgVerification(request: SaveGhgVerificationRequest): Promise<SaveGhgVerificationResponse> {
        return this.requestClient.postFormData(GhgRoutes.SaveGhgVerification, request);
    }

    public saveClientGhgVerification(request: SaveClientGhgVerificationRequest): Promise<SaveGhgVerificationResponse> {
        return this.requestClient.postFormData(GhgRoutes.SaveClientGhgVerification, request);
    }

    public generateExternalGhgVerification(request: GenerateExternalGhgVerificationRequest): Promise<SaveGhgVerificationResponse> {
        return this.requestClient.post(GhgRoutes.GenerateExternalGhgVerification, {}, request);
    }

    public saveGhgVerificationRatingFactors(request: SaveGhgVerificationRatingFactorsRequest): Promise<SaveGhgVerificationRatingFactorsResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgVerificationRatingFactors, {}, request);
    }

    public computeRevisedGhg(request: ComputeRevisedGhgRequest): Promise<ComputeRevisedGhgResponse> {
        return this.requestClient.post(`${GhgRoutes.ComputeRevisedGhg}`, {}, request);
    }

    public saveGhgRatingFactorSourceComment(request: SaveGhgRatingFactorSourceCommentRequest): Promise<GhgRatingFactorSourceCommentResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgRatingFactorSourceComment, {}, request);
    }

    public saveGhgEnergySavingEquipmentComment(request: SaveGhgEnergySavingEquipmentCommentRequest): Promise<SaveGhgEnergySavingEquipmentCommentResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgEnergySavingEquipmentComment, {}, request);
    }

    public saveGhgVerificationEexiComment(request: SaveGhgVerificationEexiCommentRequest): Promise<SaveGhgVerificationEexiCommentResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgVerificationEexiComment, {}, request);
    }

    public saveGhgVerificationCiiComment(request: SaveGhgVerificationCiiCommentRequest): Promise<SaveGhgVerificationCiiCommentResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgVerificationCiiComment, {}, request);
    }

    public saveGhgVerificationEnergySavingEquipments(request: SaveGhgVerificationEnergySavingEquipmentsRequest): Promise<SaveGhgVerificationEnergySavingEquipmentsResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgVerificationEnergySavingEquipments, {}, request);
    }

    public saveGhgVerificationEexi(request: SaveGhgVerificationEexiRequest): Promise<SaveGhgVerificationEexiResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgVerificationEexi, {}, request);
    }

    public saveGhgVerificationCii(request: SaveGhgVerificationCiiRequest): Promise<SaveGhgVerificationCiiResponse> {
        return this.requestClient.post(GhgRoutes.SaveGhgVerificationCii, {}, request);
    }

    public getHasUnacceptableCiiRatings(vesselId: number): Promise<GetHasUnacceptableCiiRatingsResponse> {
        return this.requestClient.get(GhgRoutes.GetHasUnacceptableCiiRatings, { vesselId });
    }

    public changeStatus(id: number, ghgStatusId: number, comments: string, meta: string, subtaskTypes: number[], statusSnippet: string, sendAlertEmail: boolean): Promise<void> {
        return this.requestClient.post(GhgRoutes.ChangeStatus, {}, {
            id: id,
            comments: comments,
            statusId: ghgStatusId,
            meta: meta,
            subtaskTypes: subtaskTypes,
            statusSnippet: statusSnippet,
            sendAlertEmail: sendAlertEmail
        });
    }

    public getEnvironmentalVerificationSettings(): Promise<EnvironmentalVerificationSettings> {
        return this.requestClient.get(GhgRoutes.GetEnvironmentalVerificationSettings, {});
    }

    public sendInviteToDoc(request: SendInviteToDocRequest): Promise<SendInviteToDocResponse> {
        return this.requestClient.post(GhgRoutes.SendInviteToDoc, {}, request);
    }

    public submitGhgRatingChange(request: SubmitGhgRatingChangeRequest): Promise<SubmitGhgRatingChangeResponse> {
        return  this.requestClient.post(GhgRoutes.SubmitGhgRatingChange, {}, request);
    }

    public validateEmailDomain(request: EmailValidationRequest): Promise<ValidationResult> {
        return this.requestClient.post(GhgRoutes.ValidateEmailDomain, {}, request);
    }

    public doesVesselCiiExist(request: DoesVesselCiiExistRequest): Promise<boolean> {
        return this.requestClient.post(GhgRoutes.DoesVesselCiiExist, {}, request);
    }

    public getEstimatedCii(vesselId: number): Promise<GetEstimatedCiiResponse> {
        return this.requestClient.get(`${GhgRoutes.GetEstimatedCii}/${vesselId}`, {});
    }

    public getPaintTypes(): Promise<GetPaintTypesResponse> {
        return this.requestClient.get(GhgRoutes.GetPaintTypes, {});
    }

    public getHullPreparationStandards(): Promise<GetHullPreparationStandardResponse> {
        return this.requestClient.get(GhgRoutes.GetHullPreparationStandards, {});
    }

    public getPaintManufacturers(): Promise<GetPaintManufacturersResponse> {
        return this.requestClient.get(GhgRoutes.GetPaintManufacturers, {});
    }

    public getClassificationSocieties(): Promise<GetClassificationSocietyResponse> {
        return this.requestClient.get(GhgRoutes.GetClassificationSocieties, {});
    }
}